<template>
  <div :style="{ height: `${75 + this.$options.height}px` }" class="region-time-chart">
    <div>
      <div class="header">
        <slot>
          <h2>{{ title }}</h2>
        </slot>
        <a class="reset" @click="reset()" v-if="selectedItems.length">
          <i class="material-icons refresh button">refresh</i>
        </a>
      </div>

      <a
        href="javascript:;"
        class="download-data"
        :download="downloadName"
        title="Download data"
        @mouseenter="setDownloadData('year')"
      >
        <i class="material-icons get-app">get_app</i>
      </a>
    </div>
  </div>
</template>

<script>
import mixin from './mixin';

export default {
  width: 950,
  height: 300,
  mixins: [mixin],
  group: null,
  props: {
    downloadName: {
      type: String,
      default: 'data.csv',
    },
  },
  data() {
    return {
      selectedItems: []
    }
  },
  methods: {
    createChart(ndx) {
      this.chart = dc.barChart(this.$el);
      const tRange = this.$_getTimeRange();

      const dim = ndx.dimension(d => tRange(d.date));

      const group = dim.group().reduce(
        (p, v) => {
          p[v[this.dimensionField]] = (p[v[this.dimensionField]] || 0) + v[this.aggField];
          return p;
        },
        (p, v) => {
          p[v[this.dimensionField]] = (p[v[this.dimensionField]] || 0) - v[this.aggField];
          return p;
        },
        () => {
          return {};
        },
      );
      this.$options.group = group;

      const keys = ndx
        .dimension(d => d[this.dimensionField])
        .group()
        .all()
        .map(v => v.key);

      const colorScaleComponent = d3
        .scaleOrdinal()
        .domain(keys)
        .range(this.colors.slice(0, keys.size));

      const selectStack = i => {
        return d => {
          return d.value[i] || 0;
        };
      };
      let [min, max] = d3.extent(d3.extent(ndx.all(), d => d.date));
      min = new Date(min.getFullYear() - 1, 0, 1);
      max = new Date(max.getFullYear() + 1, 0, 1);
      const numFormat = d3.format(',.1f');

      const tickFormat = this.$_getTickFormat();

      this.chart
        .width(this.$options.width)
        .height(this.$options.height)
        .margins({...this.margins, bottom: 35})
        .dimension(dim)
        .group(group, keys[0], selectStack(keys[0]))
        .gap(12)
        .colors(colorScaleComponent)
        .x(d3.scaleTime().domain([min, max]))
        .xUnits(tRange.range)
        .label(d => {
          const val = Object.values(d.data.value).reduce((s, v) => {
            return s + v;
          }, 0);
          return numFormat(val);
        })
        .mouseZoomable(false)
        .elasticY(true)
        .brushOn(false)
        .clipPadding(10)
        .legend(
          dc
            .legend()
            .x(40)
            .y(this.$options.height - 10)
            .itemHeight(10)
            .horizontal(true)
            .autoItemWidth(true)
            .legendWidth(this.$options.width - 30)
            .gap(15)
        )
        .renderLabel(true)
        .on('preRender', chart => {
          chart.xAxis().ticks(tRange);
        })
        .on('preRedraw', chart => {
          chart.xAxis().ticks(tRange);
        })
        .zoomScale([1, 1])
        .xAxis()
        .tickFormat(tickFormat);

      keys.slice(1, keys.length).forEach(key => {
        this.chart.stack(group, key, selectStack(key));
      });
      this.chart.render();

      this.chart.on('postRender', () => {
        d3.select(this.$el)
          .select('.chart-body')
          .attr('clip-path', '');

        this.$_setClickLabelEvent();
      });
    },
    // Each time a new filter is applied the click event has to be updated
    updateLabels() {
      this.$_setClickLabelEvent();
      this.$_setLabelStyles();
    },
    $_setClickLabelEvent() {
      this.chart.selectAll('.dc-legend-item').on('click', (chart) => {
        const idx = this.selectedItems.findIndex(d => d === chart.name);
        if (idx > -1) {
          this.selectedItems.splice(idx, 1);
          chart
        } else {
          this.selectedItems.push(chart.name);
        }
        this.$emit('dimensionSelected', {filters: this.selectedItems});
      });
    },
    $_setLabelStyles() {
      const sel = this.chart.selectAll('.dc-legend-item');

      [...sel._groups[0]].forEach(g => {
        const opacity = this.selectedItems.length ? '0.3' : '1';
        if (this.selectedItems.includes(g.querySelector('text').textContent)) {
          g.style.opacity = '1';
        } else {
          g.style.opacity = opacity;
        }
      });
    },
    reset() {
      this.selectedItems = [];
      this.$emit('dimensionSelected', {filters: this.selectedItems});
    },
    setDownloadData(dataType) {
      let data = '';
      switch(dataType) {
        case 'year':
          data = this.getCSVPerYear();
        break;
        default:
          data = this.getCSVData();
      }
      this.$el.querySelector('a.download-data').href = 'data:text/csv;charset=utf-8,' + data;
    },
    $_getTickFormat() {
      if (this.$attrs.interval === 'Quaterly') {
        return d => {
          if (d.getMonth() === 0) {
            return d.getFullYear();
          } else {
            return `Q${Math.floor(d.getMonth() / 3) + 1}`;
          }
        };
      } else {
        return d => {
          return d.getFullYear();
        };
      }
    },
    getCSVData() {
      const keys = [this.aggField, 'country', 'component', 'date', 'operator', 'region', 'status', 'water_depth'];
      let arr = [keys.join(',')];
      this.$attrs.ndx.allFiltered().forEach(row => {
        arr.push(
          keys
            .map(fld => {
              if (fld === 'date') {
                return row[fld].toDateString().slice(4);
              } else if (fld === 'capex') {
                return row[fld] * 1000;
              } else {
                return row[fld];
              }
            })
            .join(','),
        );
      });

      return arr.join('\n');
    },
    getCSVPerYear() {
      const data = this.$options.group.all();
      const arrays = data.map(v => v.value).map(v => Object.keys(v));
      const regions = [...new Set([].concat.apply([], arrays))].sort();
      const years = data.map(v => v.key.getFullYear());
      let arr = [['', ...years].join(',')];

      regions.forEach(region => {
        arr.push([
          region,
          ...years.map(year => {
            const d = data.find(v => v.key.getFullYear() === year);
            return d.value[region] || 0;
          })
        ]);
      })

      return arr.join('\n');
    },
    $_getTimeRange() {
      if (this.$attrs.interval === 'Quaterly') {
        return this.$_dateQuarter();
      } else {
        return d3.timeYear;
      }
    },
    $_dateQuarter() {
      return d3.timeInterval(
        function(date) {
          date.setHours(0, 0, 0, 0);
          date.setDate(1);
          date.setMonth(Math.floor(date.getMonth() / 3) * 3);
        },
        function(date, step) {
          date.setMonth(date.getMonth() + step * 3);
        },
        function(start, end) {
          const months = end.getMonth() - start.getMonth() + (end.getFullYear() - start.getFullYear()) * 12;
          return Math.ceil(months / 3);
        },
        function(date) {
          return Math.floor(date.getMonth() / 3);
        },
      );
    },
  },
};
</script>

<style scoped>
.download-data {
  position: absolute;
  right: 50px;
  top: 20px;
  color: #333;
}
</style>
<style>
.region-time-chart .dc-legend {
  font-size: 12px;
}
.dc-chart text.barLabel {
  display: none;
}
</style>
